import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RiArrowLeftSLine, RiArrowRightSLine, RiPlayCircleLine } from '@remixicon/react';
import type { DeviceInfoLayer } from '@atnt/shared';
import { cn } from '@/shared/lib/cn';
import { fileUrl as defaultFileUrl } from '../file-url';

type MediaItem = DeviceInfoLayer['media'][number];

/**
 * Device-info media collage (§6.8, viewer modal 599:37333 Setka / 599:37384 Mozaika / 599:37436 Lenta).
 * Sizes are design px of the 590 px wide modal body.
 */
export interface CollageProps {
  media: readonly MediaItem[];
  mode: DeviceInfoLayer['collage'];
  fileUrl?: (fileId: string) => string;
  /** click on an item (lightbox) */
  onOpen?: (index: number) => void;
  className?: string;
}

const isVideo = (m: MediaItem) => m.file.kind === 'video' || m.file.mime.startsWith('video/');
const duration = (sec?: number) => (sec == null ? null : `${Math.floor(sec / 60)}:${String(Math.round(sec % 60)).padStart(2, '0')}`);

export function MediaTile({ item, fileUrl = defaultFileUrl, className, onClick, overlay, label }: { item: MediaItem; fileUrl?: (id: string) => string; className?: string; onClick?: () => void; overlay?: string; label?: string }) {
  const video = isVideo(item);
  const Tag = onClick ? 'button' : 'div';
  return (
    <Tag
      {...(onClick ? { type: 'button' as const, onClick, 'aria-label': label ?? item.file.name } : {})}
      className={cn('relative flex items-center justify-center overflow-hidden rounded-8 border border-white-100 bg-white-50', onClick && 'cursor-zoom-in', className)}
    >
      {video ? (
        <video src={`${fileUrl(item.file.fileId)}#t=0.1`} preload="metadata" muted playsInline className="absolute inset-0 size-full object-cover" />
      ) : (
        <img src={fileUrl(item.file.fileId)} alt={item.file.name} loading="lazy" draggable={false} className="absolute inset-0 size-full object-cover" />
      )}
      {video && (
        <span aria-hidden className="relative flex size-32 items-center justify-center rounded-full bg-black-500 text-white">
          <RiPlayCircleLine className="size-20" />
        </span>
      )}
      {video && duration(item.file.durationSec) && (
        <span className="absolute right-6 bottom-6 rounded-4 bg-black-700 px-5 py-1 text-xs font-medium text-white tabular-nums">{duration(item.file.durationSec)}</span>
      )}
      {overlay && <span className="absolute inset-0 flex items-center justify-center bg-black-600 text-xl font-semibold text-white">{overlay}</span>}
    </Tag>
  );
}

export function Collage({ media, mode, fileUrl = defaultFileUrl, onOpen, className }: CollageProps) {
  const { t } = useTranslation('constructor');
  const [index, setIndex] = useState(0);
  if (!media.length) return null;
  const open = (i: number) => (onOpen ? () => onOpen(i) : undefined);

  if (mode === 'carousel') {
    const i = Math.min(index, media.length - 1);
    const go = (d: number) => setIndex((i + d + media.length) % media.length);
    return (
      <div className={cn('flex flex-col gap-8', className)}>
        <div className="relative h-190">
          <MediaTile item={media[i]!} fileUrl={fileUrl} onClick={open(i)} className="size-full" />
          {media.length > 1 && (
            <>
              <button type="button" onClick={() => go(-1)} aria-label={t('deviceInfo.previous')} className="absolute top-1/2 left-12 flex size-28 -translate-y-1/2 items-center justify-center rounded-14 bg-black-700 text-white hover:bg-black-800">
                <RiArrowLeftSLine className="size-18" aria-hidden />
              </button>
              <button type="button" onClick={() => go(1)} aria-label={t('deviceInfo.next')} className="absolute top-1/2 right-12 flex size-28 -translate-y-1/2 items-center justify-center rounded-14 bg-black-700 text-white hover:bg-black-800">
                <RiArrowRightSLine className="size-18" aria-hidden />
              </button>
            </>
          )}
          <span className="absolute right-10 bottom-10 rounded-6 bg-black-700 px-8 py-2 text-xs font-medium text-white tabular-nums">
            {i + 1} / {media.length}
          </span>
        </div>
        <div className="grid grid-cols-5 gap-8">
          {media.map((m, k) => (
            <button
              key={m.id}
              type="button"
              onClick={() => setIndex(k)}
              aria-label={`${k + 1} / ${media.length}`}
              aria-current={k === i}
              className={cn('h-52 overflow-hidden rounded-8 border-2', k === i ? 'border-dark-blue-1000' : 'border-transparent')}
            >
              <MediaTile item={m} fileUrl={fileUrl} className="pointer-events-none size-full" />
            </button>
          ))}
        </div>
      </div>
    );
  }

  if (mode === 'mosaic') {
    const [first, ...rest] = media;
    const side = rest.slice(0, 4);
    const more = media.length - 5;
    return (
      <div className={cn('grid h-232 grid-cols-[1fr_minmax(0,0.483fr)_minmax(0,0.483fr)] grid-rows-2 gap-8', className)}>
        <MediaTile item={first!} fileUrl={fileUrl} onClick={open(0)} className="row-span-2" />
        {side.map((m, k) => (
          <MediaTile key={m.id} item={m} fileUrl={fileUrl} onClick={open(k + 1)} overlay={k === 3 && more > 0 ? `+${more}` : undefined} />
        ))}
      </div>
    );
  }

  // grid ("Setka"): rows of up to 3, the last row's tiles stretch
  const rows: MediaItem[][] = [];
  for (let k = 0; k < media.length; k += 3) rows.push(media.slice(k, k + 3));
  return (
    <div className={cn('flex flex-col gap-8', className)}>
      {rows.map((row, r) => (
        <div key={r} className="flex gap-8">
          {row.map((m, k) => (
            <MediaTile key={m.id} item={m} fileUrl={fileUrl} onClick={open(r * 3 + k)} className="h-112 min-w-0 flex-1" />
          ))}
        </div>
      ))}
    </div>
  );
}
